import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bath,
  BedDouble,
  Building2,
  Camera,
  ChevronDown,
  Compass,
  Heart,
  Home,
  Landmark,
  MapPin,
  Ruler,
  Share2,
  SlidersHorizontal,
} from "lucide-react";
import { api, asItems, formatApiError, formatArea, formatPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useShortlist } from "@/contexts/ShortlistContext";
import { toast } from "sonner";

const FALLBACK_IMAGE = "/brand/real-place-mark.svg";

const TABS = [
  { id: "all", label: "All Properties", icon: Landmark },
  { id: "house", label: "Houses", icon: Home },
  { id: "flat", label: "Flats", icon: Building2 },
  { id: "plot", label: "Plots / Land", icon: MapPin },
];

const SORT_OPTIONS = [
  { id: "recommended", label: "Recommended" },
  { id: "newest", label: "Newest" },
  { id: "price_low", label: "Price: Low to High" },
  { id: "price_high", label: "Price: High to Low" },
];

function listingBucket(property) {
  const text = `${property?.category || ""} ${property?.subcategory || ""} ${property?.property_type || ""}`.toLowerCase();
  if (/plot|land/.test(text)) return "plot";
  if (/flat|apartment/.test(text)) return "flat";
  if (/house|villa|independent/.test(text)) return "house";
  return "all";
}

function daysAgoLabel(value) {
  if (!value) return "Updated recently";
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return "Updated recently";
  const diffDays = Math.max(0, Math.floor((Date.now() - timestamp) / (24 * 60 * 60 * 1000)));
  if (diffDays === 0) return "Updated today";
  if (diffDays === 1) return "Updated 1 day ago";
  return `Updated ${diffDays} days ago`;
}

function PropertyShowcaseCard({ property, onShare }) {
  const { isSaved, toggle } = useShortlist();
  const id = property.property_id || property.id;
  const isLand = listingBucket(property) === "plot";
  const bldg = property.building_details || property.building || {};
  const land = property.land_details || property.land || {};

  const bedrooms = property.bedrooms || bldg.bedrooms || 0;
  const bathrooms = property.bathrooms || bldg.bathrooms || 0;
  const areaVal = property.area || property.area_sqft || land.area || bldg.area || 0;
  const areaUnit = property.area_unit || land.area_unit || bldg.area_unit || "sq_ft";
  const roadWidth = property.road_width_ft || land.road_width_ft || 0;
  const facing = property.facing || bldg.facing || land.facing || "";
  const photosCount = Array.isArray(property.images) && property.images.length > 0 ? property.images.length : 1;
  const image = (Array.isArray(property.images) && property.images[0]) || property.thumbnail_url || property.public_url || FALLBACK_IMAGE;
  const saved = isSaved(id);

  const badge = property.featured
    ? { label: "Featured", cls: "bg-emerald-600 text-white" }
    : (property.boosted || property.premium || property.is_premium)
      ? { label: "Premium", cls: "bg-violet-600 text-white" }
      : { label: "New Launch", cls: "bg-teal-600 text-white" };

  const topAmenities = Array.isArray(property.amenities) ? property.amenities.slice(0, 3) : [];
  const extraAmenities = Array.isArray(property.amenities) ? Math.max(0, property.amenities.length - topAmenities.length) : 0;

  return (
    <article className="rounded-2xl border border-slate-200 bg-white shadow-[0_10px_36px_rgba(15,23,42,0.08)] overflow-hidden">
      <div className="relative">
        <Link to={`/properties/${id}`} className="block h-56 sm:h-60 overflow-hidden">
          <img
            src={image}
            alt={property.title || "Property"}
            className={`h-full w-full ${image === FALLBACK_IMAGE ? "object-contain bg-slate-100 p-8" : "object-cover"}`}
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/45 via-transparent to-transparent" />
        </Link>

        <span className={`absolute left-3 top-3 rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.08em] ${badge.cls}`}>
          {badge.label}
        </span>

        <div className="absolute right-3 top-3 flex flex-col gap-2">
          <button
            type="button"
            className="grid h-9 w-9 place-items-center rounded-full bg-white/95 text-slate-700 shadow-sm hover:bg-white"
            aria-label={saved ? "Remove from shortlist" : "Save property"}
            onClick={() => toggle(id)}
          >
            <Heart className="h-4 w-4" fill={saved ? "currentColor" : "none"} />
          </button>
          <button
            type="button"
            className="grid h-9 w-9 place-items-center rounded-full bg-white/95 text-slate-700 shadow-sm hover:bg-white"
            aria-label="Share property"
            onClick={() => onShare(property)}
          >
            <Share2 className="h-4 w-4" />
          </button>
          <Link
            to={`/properties/${id}`}
            className="grid h-9 w-9 place-items-center rounded-full bg-white/95 text-slate-700 shadow-sm hover:bg-white"
            aria-label="Open property"
          >
            <Camera className="h-4 w-4" />
          </Link>
        </div>

        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-lg bg-slate-950/80 px-2.5 py-1 text-[11px] font-bold text-white backdrop-blur-sm">
          <Camera className="h-3.5 w-3.5" /> {photosCount} Photos
        </span>
      </div>

      <div className="p-4 sm:p-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-indigo-700">
          {(property.category || "Property").toUpperCase()} · FOR SALE
        </p>
        <p className="mt-2 font-display text-[2rem] leading-none font-black text-slate-900">{formatPrice(property.price)}</p>
        <h3 className="mt-3 line-clamp-1 text-xl font-extrabold text-slate-900">{property.title || "Property title unavailable"}</h3>
        <p className="mt-1 line-clamp-1 text-sm text-slate-600">{property.description || "Verified listing with clear details."}</p>

        <div className="mt-3 flex items-center gap-2 text-sm font-semibold text-slate-600">
          <MapPin className="h-4 w-4 text-slate-500" />
          <span className="line-clamp-1">{[property.locality, property.city].filter(Boolean).join(", ") || "Location unavailable"}</span>
          {(property.verified_listing || property.verified_documents) && (
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-emerald-700">
              Verified
            </span>
          )}
        </div>

        <div className="mt-4 grid grid-cols-4 gap-2 border-y border-slate-100 py-3 text-slate-700">
          <Spec icon={BedDouble} value={bedrooms > 0 ? bedrooms : "-"} label="Bedrooms" />
          <Spec icon={Bath} value={bathrooms > 0 ? bathrooms : "-"} label="Bathrooms" />
          <Spec icon={Ruler} value={formatArea(areaVal, areaUnit)} label={areaUnit === "sq_yd" ? "sq.yd" : "sq.ft"} />
          <Spec icon={Compass} value={facing || (isLand && roadWidth ? `${roadWidth} ft` : "-")} label={facing ? "Facing" : (isLand ? "Road" : "Facing")} />
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {topAmenities.map((amenity, idx) => (
            <span key={`${amenity}-${idx}`} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
              {String(amenity).replace(/_/g, " ")}
            </span>
          ))}
          {extraAmenities > 0 && (
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-600">+{extraAmenities}</span>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-700">{property.posted_by || "Owner"}</p>
            <p className="text-xs text-slate-500">{daysAgoLabel(property.updated_at || property.modified_at || property.created_at)}</p>
          </div>
          <Link
            to={`/properties/${id}`}
            className="inline-flex items-center gap-1 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-bold text-indigo-700 transition hover:bg-indigo-100"
          >
            View Details <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </article>
  );
}

function Spec({ icon: Icon, value, label }) {
  return (
    <div className="min-w-0 text-center">
      <div className="inline-flex items-center gap-1 text-sm font-bold">
        <Icon className="h-3.5 w-3.5 text-slate-500" />
        <span className="truncate">{value}</span>
      </div>
      <p className="mt-0.5 text-[10px] font-medium text-slate-500">{label}</p>
    </div>
  );
}

export default function NewlyAddedSection() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [activeTab, setActiveTab] = useState("all");
  const [sortBy, setSortBy] = useState("recommended");
  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    // Fetch a wider set so the tab/filter experience can mirror a marketplace strip.
    api.get("/properties", { params: { limit: 12, sort: "newest" } })
      .then(({ data }) => {
        if (live) {
          setProperties(asItems(data) || []);
        }
      })
      .catch((err) => {
        if (live) {
          setError(formatApiError(err));
          setProperties([]);
        }
      })
      .finally(() => {
        if (live) {
          setLoading(false);
        }
      });

    return () => { live = false; };
  }, [reloadKey]);

  const prepared = useMemo(() => {
    const filtered = properties.filter((item) => activeTab === "all" || listingBucket(item) === activeTab);
    const sorted = [...filtered];

    if (sortBy === "price_low") {
      sorted.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
    } else if (sortBy === "price_high") {
      sorted.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
    } else if (sortBy === "newest") {
      sorted.sort((a, b) => {
        const aTime = new Date(a?.published_at || a?.created_at || 0).getTime();
        const bTime = new Date(b?.published_at || b?.created_at || 0).getTime();
        return bTime - aTime;
      });
    }

    return sorted;
  }, [properties, activeTab, sortBy]);

  const visibleProperties = prepared.slice(0, visibleCount);
  const hasMore = visibleCount < prepared.length;

  const handleShare = async (property) => {
    try {
      const id = property.property_id || property.id;
      const url = `${window.location.origin}/properties/${id}`;
      if (navigator.share) {
        await navigator.share({ title: property.title || "Property", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Property link copied");
      }
    } catch {
      // user-cancelled share should stay silent
    }
  };

  return (
    <section className="border-b border-slate-200/50 bg-slate-50/30 py-16" data-testid="newly-added-properties-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-12">
        <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_22px_80px_rgba(15,23,42,0.08)] sm:p-7 lg:p-8">
          <div className="mb-6 flex flex-col gap-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 className="font-display text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">Properties for You</h2>
                <p className="mt-1 text-sm font-medium text-slate-500">Find the perfect property from verified listings</p>
              </div>

              <div className="flex items-center gap-2">
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(event) => {
                      setSortBy(event.target.value);
                      setVisibleCount(3);
                    }}
                    className="h-11 appearance-none rounded-xl border border-slate-200 bg-white pl-4 pr-9 text-sm font-semibold text-slate-700 shadow-sm outline-none transition focus:border-indigo-400"
                  >
                    {SORT_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>{option.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                </div>

                <Button asChild variant="outline" className="h-11 rounded-xl border-slate-200 px-4 text-sm font-bold text-slate-700">
                  <Link to="/properties">
                    <SlidersHorizontal className="mr-2 h-4 w-4" /> Filters
                  </Link>
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 pb-4">
              {TABS.map(({ id, label, icon: Icon }) => {
                const active = activeTab === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      setActiveTab(id);
                      setVisibleCount(3);
                    }}
                    className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold transition ${active ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-800"}`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {loading && (
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-[560px] rounded-2xl border border-slate-200 shimmer" />
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="rounded-2xl border border-destructive/20 bg-destructive/5 py-12 text-center" role="alert">
              <p className="text-sm font-bold text-slate-900">Property feed could not be loaded.</p>
              <p className="mt-1 text-xs font-medium text-destructive">{error}</p>
              <Button type="button" variant="outline" size="sm" className="mt-4" onClick={() => setReloadKey((value) => value + 1)}>Try again</Button>
            </div>
          )}

          {!loading && !error && visibleProperties.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 py-12 text-center">
              <p className="text-sm font-semibold text-slate-600">No listings found in this category yet.</p>
            </div>
          )}

          {!loading && !error && visibleProperties.length > 0 && (
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {visibleProperties.map((property) => (
                <PropertyShowcaseCard
                  key={property.property_id || property.id}
                  property={property}
                  onShare={handleShare}
                />
              ))}
            </div>
          )}

          {!loading && !error && hasMore && (
            <div className="mt-8 flex justify-center">
              <Button
                type="button"
                variant="outline"
                className="h-11 min-w-64 rounded-xl border-slate-300 px-6 text-sm font-bold text-slate-700"
                onClick={() => setVisibleCount((current) => current + 3)}
              >
                Load More Properties <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
